import { ApiResponse, PaginatedApiResponse } from '@/types/api.types';
import baseApi from './RootBaseApi';
import {
  HarborResponse,
  HarborListQueryParams,
  FeaturedHarborResponse,
  UpdateFeaturedPayload,
  MediaPayload,
  CoastalAreaResponse,
  HarborDeletionRequestPayload,
  HarborDeletionListQueryParams,
  PaginatedHarborDeletionResponse,
  HarborTranslationPayload,
  HarborTranslations,
  HarborAccessInformation,
  UpdateHarborAccessInformationPayload,
  TranslateHarborAccessInformationPayload,
  HarborAccessInformationTranslations,
  MyHarborListResponse,
  HarborKYCData,
  BulkImportHarborsPayload,
  BulkImportHarborsResult,
  BulkUpdateHarborsPayload,
  BulkUpdateHarborsResult,
} from '@/types/harbor-management.types';
import { KYCType } from '@/constant';
import {
  HarborRequestData,
  HarborRequestListQueryParams,
  PaginatedHarborRequestResponse,
} from '@/types/harbor-requests.types';
import { PublicListValue } from '@/types/list-value.types';

export const HarborManagementService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getHarbors: builder.query<
      PaginatedApiResponse<HarborResponse[]>,
      HarborListQueryParams
    >({
      query: (params) => ({
        url: 'harbors',
        method: 'GET',
        params,
      }),
      transformResponse: (response: PaginatedApiResponse<HarborResponse[]>) =>
        response,
      providesTags: ['Harbors'],
      keepUnusedDataFor: 0,
    }),

    // Harbor Admin specific list (same response type)
    getMyHarbors: builder.query<MyHarborListResponse, HarborListQueryParams>({
      query: (params) => ({
        url: 'harbors/my',
        method: 'GET',
        params,
      }),
      transformResponse: (response: MyHarborListResponse) => response,
      providesTags: ['Harbors'],
      keepUnusedDataFor: 0,
    }),

    getHarborById: builder.query<HarborResponse, string>({
      query: (harbor_id) => ({
        url: `harbors/${harbor_id}`,
        method: 'GET',
      }),
      transformResponse: (response: HarborResponse) => response,
      providesTags: (result, error, harbor_id) => [
        { type: 'Harbors', id: harbor_id },
      ],
      keepUnusedDataFor: 0,
    }),

    createHarbor: builder.mutation<HarborResponse, FormData>({
      query: (data) => ({
        url: 'harbors',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Harbors'],
    }),

    updateHarbor: builder.mutation<
      ApiResponse<HarborResponse>,
      { harbor_id: string; data: FormData }
    >({
      query: ({ harbor_id, data }) => ({
        url: `harbors/${harbor_id}`,
        method: 'PATCH',
        data,
      }),
      transformResponse: (response: ApiResponse<HarborResponse>) => response,
      invalidatesTags: ['Harbors'],
    }),

    deleteHarbor: builder.mutation<ApiResponse<void>, string>({
      query: (harbor_id) => ({
        url: `harbors/${harbor_id}`,
        method: 'DELETE',
      }),
      transformResponse: (response: ApiResponse<void>) => response,
      invalidatesTags: ['Harbors'],
    }),

    getFeaturedHarbors: builder.query<
      PaginatedApiResponse<FeaturedHarborResponse[]>,
      Record<string, never>
    >({
      query: () => ({
        url: '/featured-listings/harbor/featured',
        method: 'GET',
      }),
      transformResponse: (
        response: PaginatedApiResponse<FeaturedHarborResponse[]>
      ) => response,
      providesTags: ['FeaturedHarbors'],
      keepUnusedDataFor: 0,
    }),

    updateFeaturedHarbor: builder.mutation<
      ApiResponse<void>,
      UpdateFeaturedPayload
    >({
      query: ({ harborId, featured }) => ({
        url: '/featured-listings/harbor/featured',
        method: 'PATCH',
        data: { harborId, featured },
      }),
      transformResponse: (response: ApiResponse<void>) => response,
      invalidatesTags: ['FeaturedHarbors', 'Harbors'],
    }),

    createMediaHarborId: builder.mutation<void, MediaPayload>({
      query: ({ id, media }) => {
        const formData = new FormData();
        media.forEach((file) => {
          formData.append(`media[]`, file);
        });
        return {
          url: `/harbors/${id}/media`,
          method: 'POST',
          data: formData,
        };
      },
      invalidatesTags: ['Harbors'],
    }),

    getCoastalArea: builder.query<CoastalAreaResponse[], { language?: string }>(
      {
        query: (params) => ({
          url: '/list-values/coastal-area/public',
          method: 'GET',
          headers: params?.language ? { 'x-lang': params.language } : undefined,
        }),
        transformResponse: (response: PublicListValue[]) =>
          response.map((item) => ({
            id: item.id,
            name: item.key,
            description: item.description || '',
            label: item.label || item.name || item.key,
          })),
        providesTags: ['Harbors'],
      }
    ),

    requestHarborDeletion: builder.mutation<
      ApiResponse<void>,
      { harbor_id: string; data: HarborDeletionRequestPayload }
    >({
      query: ({ harbor_id, data }) => ({
        url: `harbors/${harbor_id}/request-deletion`,
        method: 'POST',
        data,
      }),
      transformResponse: (response: ApiResponse<void>) => response,
      invalidatesTags: ['Harbors', 'HarborDeletionRequests'],
    }),

    approveHarborDeletion: builder.mutation<ApiResponse<void>, string>({
      query: (harbor_id) => ({
        url: `harbors/${harbor_id}/approve-deletion`,
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<void>) => response,
      invalidatesTags: ['Harbors', 'HarborDeletionRequests'],
    }),

    rejectHarborDeletion: builder.mutation<ApiResponse<void>, string>({
      query: (harbor_id) => ({
        url: `harbors/${harbor_id}/reject-deletion`,
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<void>) => response,
      invalidatesTags: ['Harbors', 'HarborDeletionRequests'],
    }),

    recoverHarbor: builder.mutation<ApiResponse<void>, string>({
      query: (harbor_id) => ({
        url: `harbors/${harbor_id}/recover`,
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<void>) => response,
      invalidatesTags: ['Harbors', 'HarborDeletionRequests'],
    }),

    getHarborDeletionRequests: builder.query<
      PaginatedHarborDeletionResponse,
      HarborDeletionListQueryParams
    >({
      query: (params) => {
        const url =
          params.status === 'deleted'
            ? 'harbors/deleted-harbors'
            : 'harbors/deletion-requests';

        const { ...cleanParams } = params;

        return {
          url,
          method: 'GET',
          params: cleanParams,
        };
      },
      transformResponse: (response: PaginatedHarborDeletionResponse) =>
        response,
      providesTags: ['HarborDeletionRequests'],
      keepUnusedDataFor: 0,
    }),
    translateHarborContent: builder.mutation<
      HarborTranslations,
      HarborTranslationPayload
    >({
      query: (data) => ({
        url: `harbors/translate`,
        method: 'POST',
        data,
      }),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      transformResponse: (response: any) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const maybeWrapper = response as any;
        const translations = maybeWrapper?.translations || maybeWrapper?.data?.translations || {};
        const message = maybeWrapper?.message || maybeWrapper?.data?.message;
        const warning_message = maybeWrapper?.warning_message || maybeWrapper?.data?.warning_message;
        
        return {
          translations,
          message,
          warning_message,
        };
      },
    }),

    getHarborAccessInformation: builder.query<HarborAccessInformation, string>({
      query: (harbor_id) => ({
        url: `harbors/${harbor_id}/access-information`,
        method: 'GET',
      }),
      transformResponse: (response: HarborAccessInformation) => response,
      providesTags: (result, error, harbor_id) => [
        { type: 'Harbors', id: `${harbor_id}-access-information` },
      ],
      keepUnusedDataFor: 0,
    }),

    updateHarborAccessInformation: builder.mutation<
      HarborAccessInformation,
      { harbor_id: string; data: UpdateHarborAccessInformationPayload }
    >({
      query: ({ harbor_id, data }) => ({
        url: `harbors/${harbor_id}/access-information`,
        method: 'PATCH',
        data,
      }),
      transformResponse: (response: HarborAccessInformation) => response,
      invalidatesTags: (result, error, { harbor_id }) => [
        { type: 'Harbors', id: `${harbor_id}-access-information` },
      ],
    }),

    translateHarborAccessInformation: builder.mutation<
      HarborAccessInformationTranslations,
      TranslateHarborAccessInformationPayload
    >({
      query: (data) => ({
        url: `harbors/access-information/translate`,
        method: 'POST',
        data,
      }),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      transformResponse: (response: any) => {
        const translations = response?.translations || {};
        const message = response?.message;
        const warning_message = response?.warning_message;
        return { translations, message, warning_message };
      },
    }),

    // Harbor Requests (Create/Update requests) management
    getHarborRequests: builder.query<
      PaginatedHarborRequestResponse,
      HarborRequestListQueryParams
    >({
      query: (params) => ({
        url: `harbors/approval-requests`,
        method: 'GET',
        params,
      }),
      transformResponse: (response: PaginatedHarborRequestResponse) => response,
      providesTags: (result) =>
        result?.data && Array.isArray(result.data)
          ? [
              ...result.data.map((item) => ({
                type: 'HarborRequests' as const,
                id: item.id,
              })),
              { type: 'HarborRequests' as const, id: 'LIST' },
            ]
          : [{ type: 'HarborRequests' as const, id: 'LIST' }],
      keepUnusedDataFor: 0,
    }),

    getHarborRequestById: builder.query<HarborRequestData, string>({
      query: (id) => ({
        url: `harbors/approval-requests/${id}`,
        method: 'GET',
      }),
      transformResponse: (response: HarborRequestData) => response,
      providesTags: (result, error, id) => [{ type: 'HarborRequests', id }],
    }),

    approveHarborRequest: builder.mutation<ApiResponse<void>, string>({
      query: (id) => ({
        url: `harbors/${id}/approve`,
        method: 'PATCH',
      }),
      transformResponse: (response: ApiResponse<void>) => response,
      invalidatesTags: (result, error, id) => [
        { type: 'HarborRequests', id },
        { type: 'HarborRequests', id: 'LIST' },
      ],
    }),

    rejectHarborRequest: builder.mutation<
      ApiResponse<void>,
      { id: string; reason?: string }
    >({
      query: ({ id, reason }) => ({
        url: `harbors/${id}/reject`,
        method: 'PATCH',
        data: reason ? { reason } : undefined,
      }),
      transformResponse: (response: ApiResponse<void>) => response,
      invalidatesTags: (result, error, { id }) => [
        { type: 'HarborRequests', id },
        { type: 'HarborRequests', id: 'LIST' },
      ],
    }),

    getHarborKYC: builder.query<ApiResponse<HarborKYCData>, string>({
      query: (entityId) => ({
        url: `kyc/entity/${KYCType.HARBOR}/${entityId}`,
        method: 'GET',
      }),
      transformResponse: (response: ApiResponse<HarborKYCData>) => response,
      providesTags: (result, error, entityId) => [
        { type: 'Harbors', id: `${entityId}-kyc` },
      ],
      keepUnusedDataFor: 0,
    }),

    bulkImportHarbors: builder.mutation<
      BulkImportHarborsResult,
      BulkImportHarborsPayload
    >({
      query: (body) => ({
        url: 'harbors/bulk-import',
        method: 'POST',
        data: body,
      }),
      invalidatesTags: ['Harbors'],
    }),

    bulkUpdateHarbors: builder.mutation<
      BulkUpdateHarborsResult,
      BulkUpdateHarborsPayload
    >({
      query: ({ file, dryRun }) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: 'harbors/bulk-update',
          method: 'POST',
          data: formData,
          params: { dryRun },
        };
      },
      invalidatesTags: (result, error, { dryRun }) =>
        dryRun ? [] : ['Harbors'],
    }),

    exportHarborsCsv: builder.query<
      { fileUrl: string },
      Omit<HarborListQueryParams, 'page' | 'limit'> & {
        regionIds?: string[];
        coastalAreaNames?: string[];
      }
    >({
      query: (params) => ({
        url: 'harbors/export/csv',
        method: 'GET',
        params,
      }),
      transformResponse: (response: { fileUrl: string }) => response,
      keepUnusedDataFor: 0,
    }),
  }),
});

export const {
  useGetHarborsQuery,
  useGetMyHarborsQuery,
  useGetHarborByIdQuery,
  useCreateHarborMutation,
  useUpdateHarborMutation,
  useDeleteHarborMutation,
  useGetFeaturedHarborsQuery,
  useUpdateFeaturedHarborMutation,
  useCreateMediaHarborIdMutation,
  useGetCoastalAreaQuery,
  useRequestHarborDeletionMutation,
  useApproveHarborDeletionMutation,
  useRejectHarborDeletionMutation,
  useRecoverHarborMutation,
  useGetHarborDeletionRequestsQuery,
  useTranslateHarborContentMutation,
  useGetHarborAccessInformationQuery,
  useUpdateHarborAccessInformationMutation,
  useTranslateHarborAccessInformationMutation,
  useGetHarborRequestsQuery,
  useGetHarborRequestByIdQuery,
  useApproveHarborRequestMutation,
  useRejectHarborRequestMutation,
  useGetHarborKYCQuery,
  useExportHarborsCsvQuery,
  useBulkImportHarborsMutation,
  useBulkUpdateHarborsMutation,
} = HarborManagementService;
